export type Role = "user" | "director" | "ceo" | "admin";

export interface CurrentUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  role: Role;
  department_id: string | null;
  department_name: string | null;
  /** Department ids this user leads as a CEO (empty for everyone else).
   *  Includes the legacy `User.department` for back-compat. Used by
   *  pickers to scope to "my departments" instead of all-org. */
  led_department_ids: string[];
  phone: string;
  position: string;
  bio: string;
  avatar_url: string | null;
  is_active: boolean;
  must_change_password: boolean;
  is_director_level: boolean;
  is_ceo_level: boolean;
  is_admin_level: boolean;
}

// ---------------------------------------------------------------------------
// Domain shapes — match what `apps/private/api/v1/serializers.py` returns.
// ---------------------------------------------------------------------------

export type WorkflowState =
  | "owner_only"
  | "private_only"
  | "pending_review"
  | "pending_ceo_review"
  | "published"
  | "rejected";

export type FileType = "document" | "video" | "audio" | "image" | "other";

export interface Tag {
  id: string;
  name: string;
}

export interface Category {
  id: string;
  name: string;
  code: string;
}

/** Shape returned by GET /api/v1/documents/ (list endpoint — trimmed). */
export interface DocumentRow {
  id: string;
  title: string;
  file_type: FileType;
  file_type_display: string;
  file_url: string | null;
  file_extension: string;
  thumbnail_url: string | null;
  category: string;
  category_display: string;
  workflow_state: WorkflowState;
  workflow_state_display: string;
  requested_visibility: WorkflowState | null;
  is_featured: boolean;
  is_verified: boolean;
  file_size_bytes: number | null;
  /** How many physical files this resource bundles (DocumentFile rows).
   *  1 = single attachment (usual case); >1 = a bundle the library card
   *  flags with a "📎 N" pip so users spot multi-file resources at a glance. */
  file_count: number;
  download_count: number;
  owner_id: string | null;
  owner_name: string | null;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
}

/** One physical attachment in a Document's file bundle. */
export interface DocumentFileRow {
  id: string;
  file_url: string | null;
  original_name: string;
  size_bytes: number;
  content_type: string;
  position: number;
  uploaded_at: string;
}

/** Shape returned by GET /api/v1/documents/<id>/ — list fields + description,
 *  tags, owner detail, and the full file-attachment list. */
export interface DocumentDetail extends DocumentRow {
  description: string;
  tags: Tag[];
  owner: CurrentUser | null;
  subscribed: boolean;
  comment_count: number;
  version_count: number;
  /** Every physical file in this resource's bundle, ordered by position.
   *  The first entry is the "primary" (mirrored to `file_url` on the row). */
  files: DocumentFileRow[];
}

export interface DocumentCommentRow {
  id: string;
  document: string;
  reply_to: number | null;
  body: string;
  author_id: string | null;
  author_name: string | null;
  author: CurrentUser | null;
  is_hidden: boolean;
  can_edit: boolean;
  created_at: string;
  edited_at: string | null;
}

export interface DocumentVersionRow {
  id: string;
  document: string;
  version_number: number;
  file_url: string | null;
  file_size_bytes: number;
  notes: string;
  uploaded_by: number | null;
  uploaded_by_name: string | null;
  uploaded_at: string;
}

/** DRF PageNumberPagination envelope. */
export interface Paginated<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

/** Cheap aggregate from GET /api/v1/documents/counts/. */
export interface DocumentCounts {
  total: number;
  published: number;
  pending_review: number;
  pending_ceo_review: number;
}

// ---------------------------------------------------------------------------
// Article / Event / User / Department — list-page shapes only.
// ---------------------------------------------------------------------------

export interface ArticleRow {
  id: string;
  title: string;
  slug: string;
  image_url: string | null;
  status: WorkflowState;
  status_display: string;
  requested_visibility: WorkflowState | null;
  category: string;
  category_display: string;
  view_count: number;
  is_featured: boolean;
  reading_time: number;
  owner_id: string | null;
  owner_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface ArticleDetail extends ArticleRow {
  content: string;             // rich-text HTML
  owner: CurrentUser | null;
  subscribed: boolean;
  comment_count: number;
}

export interface ArticleCommentRow {
  id: string;
  article: string;
  reply_to: number | null;
  body: string;
  author_id: string | null;
  author_name: string | null;
  author: CurrentUser | null;
  is_hidden: boolean;
  can_edit: boolean;
  created_at: string;
  edited_at: string | null;
}

export type EventType =
  | "conference" | "workshop" | "training" | "webinar" | "field" | "other";

export interface EventRow {
  id: string;
  title: string;
  summary: string;
  event_type: EventType;
  event_type_display: string;
  image_url: string | null;
  location: string;
  start_at: string;
  end_at: string | null;
  registration_url: string;
  is_published: boolean;
  is_featured: boolean;
  needs_ceo_approval: boolean;
  is_past: boolean;
  owner_id: string | null;
  owner_name: string | null;
  created_at: string;
  updated_at: string;
}

/** One photo in an event's post-hoc memory gallery. */
export interface EventMemoryRow {
  id: string;
  image_url: string | null;
  caption: string;
  position: number;
  uploaded_by: string | null;
  uploaded_by_name: string | null;
  /** Flat-feed fields — useful when listing recent memories across events. */
  event_id: string;
  event_title: string;
  uploaded_at: string;
}

export interface EventDetail extends EventRow {
  description: string;
  owner: CurrentUser | null;
  memories: EventMemoryRow[];
}

// ---------------------------------------------------------------------------
// Forum — Threads + Posts (see backend Thread / Post models).
// ---------------------------------------------------------------------------

export type ThreadCategory =
  | "general" | "kpi" | "verification" | "climate_finance"
  | "carbon_market" | "resources" | "announcement";

export interface ThreadRow {
  id: string;
  title: string;
  category: ThreadCategory;
  category_display: string;
  is_pinned: boolean;
  is_locked: boolean;
  author_id: string | null;
  author_name: string | null;
  post_count: number;
  last_post_at: string;
  subscribed: boolean;
  /** Posts in this thread the viewer hasn't read yet. Server-counted
   *  against ThreadRead.last_read_at; own posts never count as unread. */
  unread_count: number;
  created_at: string;
  updated_at: string | null;
}

export interface ThreadDetail extends ThreadRow {
  body: string;
  author: CurrentUser | null;
}

// ---------------------------------------------------------------------------
// Direct messages (1-on-1 chat)
// ---------------------------------------------------------------------------
export interface DMUserStub {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  avatar_url: string | null;
}

export interface DMLastMessage {
  id: string;
  sender_id: string;
  content: string;
  created_at: string;
}

export interface ConversationRow {
  id: string;
  other_user: DMUserStub | null;
  last_message: DMLastMessage | null;
  unread_count: number;
  created_at: string;
  updated_at: string;
}

export interface DMAttachment {
  id: string;
  file_url: string | null;
  original_name: string;
  content_type: string;
  size: number;
  human_size: string;
  is_image: boolean;
  uploaded_at: string;
}

export interface DirectMessageRow {
  id: string;
  conversation: string;
  content: string;
  sender_id: string;
  sender_name: string;
  sender: CurrentUser | null;
  is_deleted: boolean;
  attachments: DMAttachment[];
  /** True once the OTHER participant has opened the conversation past
   *  this message's timestamp. Drives the ✓ vs ✓✓ tick on own bubbles. */
  read_by_other: boolean;
  created_at: string;
}

export interface PostAttachment {
  id: string;
  file_url: string | null;
  original_name: string;
  content_type: string;
  size: number;
  human_size: string;
  is_image: boolean;
  uploaded_at: string;
}

export interface PostReactionSummary {
  emoji: string;
  count: number;
  by_me: boolean;
}

/** Quick-pick emojis shown inline on the picker without scrolling
 *  through categories. These are the "everyday" reactions you can fire
 *  with one tap. The full curated set (~35 emojis) comes from the
 *  server via GET /api/v1/reactions/catalog/ — see useReactionsCatalog
 *  — so the picker stays in sync with PostReaction.EMOJI_GROUPS on
 *  the backend without us hardcoding it twice. */
export const REACTION_EMOJIS = ["👍", "🙏", "🎉", "✅", "👀"] as const;
/** Loosened to plain string — the catalog is now server-driven and may
 *  grow. Backend validates the actual emoji on every react call. */
export type ReactionEmoji = string;

export interface PostReplyPreview {
  id: string;
  author_name: string;
  excerpt: string;
  is_hidden: boolean;
}

export interface PostRow {
  id: string;
  thread: string;
  content: string;
  author_id: string | null;
  author_name: string | null;
  author: CurrentUser | null;
  is_edited: boolean;
  edited_at: string | null;
  is_hidden: boolean;
  can_edit: boolean;
  /** Parent post id when this is a reply, null otherwise. */
  reply_to: number | null;
  /** Small "quoted parent" preview, populated when reply_to is set. */
  reply_to_preview: PostReplyPreview | null;
  attachments: PostAttachment[];
  reactions: PostReactionSummary[];
  created_at: string;
}

// ---------------------------------------------------------------------------
// Indicators — CEO-authored metrics tracked over time.
// ---------------------------------------------------------------------------

export type IndicatorDirection = "increasing" | "decreasing" | "stable";
/** Cadence the indicator is measured at. Drives the date-picker shape +
 *  the way the date is formatted in the UI ("Q1 2026" / "Mar 2026" / …). */
export type IndicatorPeriod = "annual" | "quarterly" | "monthly" | "daily";

export interface IndicatorRow {
  id: string;
  name: string;
  unit: string;
  direction: IndicatorDirection;
  direction_display: string;
  period: IndicatorPeriod;
  period_display: string;
  /** Required M2M — every indicator has at least one category id, with
   *  parallel display names so list cards don't need a second fetch. */
  categories: string[];
  category_names: string[];
  workflow_state: WorkflowState;
  workflow_state_display: string;
  requested_visibility: WorkflowState | null;
  owner_id: string | null;
  owner_name: string | null;
  /** How many observations have been recorded for this indicator. */
  value_count: number;
  /** Most recent observation's value (stringified decimal) — null if no
   *  values yet. Stringified so we don't lose decimal precision on the
   *  JSON round-trip. */
  latest_value: string | null;
  /** ISO date (YYYY-MM-DD) of the latest observation, or null. */
  latest_date: string | null;
  created_at: string;
  updated_at: string;
}

export interface IndicatorValueRow {
  id: string;
  indicator: string;
  date: string;          // YYYY-MM-DD
  value: string;         // stringified decimal
  target: string | null;
  note: string;
  recorded_by: string | null;
  recorded_by_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface IndicatorDetail extends IndicatorRow {
  description: string;
  owner: CurrentUser | null;
  values: IndicatorValueRow[];
}

// ---------------------------------------------------------------------------
// Climate Projects — CEO-authored programme work (Green Legacy etc.).
// ---------------------------------------------------------------------------

export type ClimateProjectStatus = "pipeline" | "active" | "completed";
export type ClimateProjectType   = "project" | "action";

export interface ClimateProjectRow {
  id: string;
  title: string;
  initiative: string;
  summary: string;
  image_url: string | null;
  location: string;
  status: ClimateProjectStatus;
  status_display: string;
  project_type: ClimateProjectType;
  project_type_display: string;
  finance_amount: string | null;     // decimal-as-string
  finance_currency: string;
  funding_source: string;
  start_date: string | null;         // YYYY-MM-DD
  end_date: string | null;
  project_url: string;
  owner_id: string | null;
  owner_name: string | null;
  is_published: boolean;
  is_featured: boolean;
  created_at: string;
  updated_at: string;
}

export interface ClimateProjectDetail extends ClimateProjectRow {
  owner: CurrentUser | null;
}

export interface UserRow {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  role: Role;
  department_id: string | null;
  department_name: string | null;
  /** Dept ids this CEO leads (Department.ceo reverse). Empty for non-CEOs. */
  led_department_ids: string[];
  position: string;
  is_active: boolean;
  must_change_password: boolean;
  is_director_level: boolean;
  is_ceo_level: boolean;
  is_admin_level: boolean;
  last_login: string | null;
}
